import React from 'react'
import { withRouter } from 'react-router-dom'
import { reduxForm, Field, formValues } from 'redux-form'
import PropTypes from 'prop-types'
import { cx } from '@emotion/css'
import {
  FormLabel,
  SingleSelect,
  SingleOption,
  Button,
  RadioInputGroup,
  RadioInput,
  TextInput,
  Notification,
  FormErrorNotification,
  requiredField,
  FormFieldset,
  trackEvent,
} from '@offerzen/pinata-shame'

import { previousOnboardingUrl } from '../../helpers/onboardingSteps'
import CardFooterNav from '../CardFooterNav'

import styles from './styles'

const validators = {
  required: requiredField('Required'),
}

const AreaRolesForm = props => (
  <form onSubmit={props.handleSubmit}>
    <If condition={props.error && props.error !== 'Please add at least one role before continuing.'}>
      <FormErrorNotification text={props.error} />
    </If>

    <FormFieldset legend="What is your current experience in the tech industry?" className={styles.fieldSet}>
      <Field
        component={SingleSelect}
        id="yearsOfExperience"
        placeholder="- select years of experience -"
        name="primary_role_experience"
        dataTest="primary_role_experience"
        validate={validators.required}
        label="Total work experience"
        selectClassName={styles.field}>
        <For each="option" of={props.yearsExperienceList}>
          <SingleOption label={option} value={option} key={option} />
        </For>
      </Field>

      <Field
        component={SingleSelect}
        id="areaOfWork"
        placeholder="- select an area of work -"
        name="primary_role"
        dataTest="primary_role"
        validate={validators.required}
        label="Primary area of work"
        labelClassName={styles.primaryRoleLabel}
        selectClassName={cx(styles.field, styles.primaryRole)}
        onChange={(_e, value) => {
          if (value === 'other') {
            trackEvent('Candidate: profile action', {
              action: 'selected other area of work',
              source: 'onboarding',
            })
          }
        }}>
        <For each="primaryRole" of={props.primaryRoles}>
          <SingleOption label={primaryRole.label} value={primaryRole.value} key={primaryRole.value} />
        </For>
      </Field>

      <If condition={props.primaryRole === 'other'}>
        <Field
          component={RadioInputGroup}
          name="work_in_tech"
          label="Tell us more about your area of work"
          validate={validators.required}
          className={styles.workType}
          onChange={(_e, value) => {
            if (value === 'not_in_tech') {
              trackEvent('Candidate: profile action', {
                action: 'selected non-tech',
                source: 'onboarding',
              })
            }
            if (value === 'different_tech_role') {
              trackEvent('Candidate: profile action', {
                action: 'selected different type of tech',
                source: 'onboarding',
              })
            }
          }}>
          <RadioInput value="different_tech_role" label="I work in a different type of tech role" />
          <RadioInput value="not_in_tech" label="I don’t work in tech" />
        </Field>
      </If>

      <If condition={props.primaryRole === 'other' && (props.workInTech === 'not_in_tech' || props.workInTech === 'different_tech_role')}>
        <FormLabel htmlFor="otherRole" label="Specify your area of work" className={styles.formLabel} />
        <Field
          component={TextInput}
          name="other_role"
          id="otherRole"
          className={cx(styles.field, styles.otherRole)}
          validate={validators.required}
          placeholder="Enter an area of work"
        />
        <If condition={props.workInTech === 'not_in_tech'}>
          <Notification theme="light" className={styles.notification}>
            <p>
              Companies using OfferZen are only looking to hire candidates who work in the tech industry. Specify your area of work to help
              us understand your experience.
            </p>
          </Notification>
        </If>
      </If>
    </FormFieldset>

    <FormFieldset legend="What roles would you like to pursue in your next job?" className={styles.fieldSet}>
      {props.children}
      <Button variant="outlined" label="Add role" size="medium" onClick={props.addBlankJobExperienceItem} />

      <If condition={props.error && props.error === 'Please add at least one role before continuing.' && !props.hasSubmittedJobExperiences}>
        <Notification theme="error" className={styles.errorNotification}>
          {props.error}
        </Notification>
      </If>
    </FormFieldset>
    <CardFooterNav
      loading={props.submitting || props.loadingCandidate}
      navigateBack={() => props.history.push(previousOnboardingUrl(props.location.pathname))}
    />
  </form>
)

AreaRolesForm.propTypes = {
  primaryRoles: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  yearsExperienceList: PropTypes.arrayOf(PropTypes.string),
  addBlankJobExperienceItem: PropTypes.func,
  loadingCandidate: PropTypes.bool,
  handleSubmit: PropTypes.func,
  error: PropTypes.string,
  primaryRole: PropTypes.string,
  hasSubmittedJobExperiences: PropTypes.bool,
}

const isInTech = workInTech => {
  if (workInTech === 'different_tech_role') return true
  if (workInTech === 'not_in_tech') return false
  return null
}

export default reduxForm({
  form: 'areaRoles',
  onSubmit: (values, _, props) =>
    new Promise((resolve, reject) => {
      const newValues = {
        ...values,
        work_in_tech: isInTech(values.work_in_tech),
      }
      props.submitJobExperience(newValues, resolve, reject)
    }),
})(
  formValues({
    primaryRole: 'primary_role',
    workInTech: 'work_in_tech',
  })(withRouter(AreaRolesForm)),
)
